import React, { useEffect, useRef } from 'react';

const BlackHoleWebGL = ({ accelerate = false, interactive = true }) => {
  const canvasRef = useRef(null);
  const animationIdRef = useRef(null);
  const accelerateRef = useRef(accelerate);
  const interactiveRef = useRef(interactive);

  // Обновляем ссылки когда props меняются
  useEffect(() => {
    accelerateRef.current = accelerate;
  }, [accelerate]);

  useEffect(() => {
    interactiveRef.current = interactive;
  }, [interactive]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const bgUrl = 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?ixlib=rb-1.2.1&auto=format&fit=crop&w=1427&q=80';
    const blackholeMass = 120000;
    let curblackholeMass = 1800;
    const beforeClickMaxMass = 2200;
    let canvas = canvasRef.current;
    let gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    canvas.width = window.innerWidth >= window.innerHeight ? window.innerWidth : window.innerHeight;
    canvas.height = window.innerWidth >= window.innerHeight ? window.innerWidth : window.innerHeight;

    let mouse = {x: canvas.width/2, y: canvas.height/2, moved: false};
    const startTime = new Date().getTime();
    let currentTime = 0;
    
    // Состояние для ускорения при нажатии
    let isPressed = false;
    let rotationAngle = 0; // Накопленный угол вращения
    let baseRotationSpeed = 0.05; // Базовая скорость вращения

    const handleMouseDown = () => { 
      // Только если интерактивность включена
      if (interactiveRef.current) {
        isPressed = true; 
      }
    };
    const handleMouseUp = () => { isPressed = false; };
    const handleTouchStart = () => { 
      if (interactiveRef.current) {
        isPressed = true; 
      }
    };
    const handleTouchEnd = () => { isPressed = false; };

    // Добавляем слушатели для мыши и тача
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    // WebGL setup
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1.0, -1.0, 1.0, -1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0
    ]), gl.STATIC_DRAW);

    // Shaders
    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      void main() {
        gl_Position = vec4(a_position, 0, 1);
        v_texCoord = a_texCoord;
      }
    `;

    const fragmentShaderSource = `
      #ifdef GL_ES
      precision mediump float;
      #endif
      #define PI 3.14159265359
      uniform sampler2D u_image;
      varying vec2 v_texCoord;
      uniform vec2 u_resolution;
      uniform vec2 u_mouse;
      uniform float u_mass;
      uniform float u_rotation;

      vec2 rotate(vec2 mt, vec2 st, float angle, float rotation){
        float cos_val = cos((angle + rotation) * PI);
        float sin_val = sin((angle + rotation) * PI * 0.2);
        float nx = (cos_val * (st.x - mt.x)) + (sin_val * (st.y - mt.y)) + mt.x;
        float ny = (cos_val * (st.y - mt.y)) - (sin_val * (st.x - mt.x)) + mt.y;
        return vec2(nx, ny);
      }

      void main() {
        vec2 st = vec2(gl_FragCoord.x, u_resolution.y - gl_FragCoord.y)/u_resolution;
        vec2 mt = vec2(u_mouse.x, u_resolution.y - u_mouse.y)/u_resolution;
        float dx = st.x - mt.x;
        float dy = st.y - mt.y;
        float dist = sqrt(dx * dx + dy * dy);
        float pull = u_mass / (dist * dist);
        vec3 color = vec3(0.0);
        vec2 r = rotate(mt, st, pull, u_rotation);
        vec4 imgcolor = texture2D(u_image, r);
        color = vec3(
          (imgcolor.x - (pull * 0.25)),
          (imgcolor.y - (pull * 0.25)),
          (imgcolor.z - (pull * 0.25))
        );
        gl_FragColor = vec4(color,1.);
      }
    `;

    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const locationOfResolution = gl.getUniformLocation(program, "u_resolution");
    const locationOfMouse = gl.getUniformLocation(program, "u_mouse");
    const locationOfMass = gl.getUniformLocation(program, "u_mass");
    const locationOfRotation = gl.getUniformLocation(program, "u_rotation");

    gl.uniform2f(locationOfResolution, canvas.width, canvas.height);
    gl.uniform2f(locationOfMouse, mouse.x, mouse.y);
    gl.uniform1f(locationOfMass, curblackholeMass*0.00001);
    gl.uniform1f(locationOfRotation, rotationAngle);

    const texCoordLocation = gl.getAttribLocation(program, "a_texCoord");
    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1.0, -1.0, 1.0, -1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0
    ]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.src = bgUrl;
    
    let lastFrameTime = performance.now();

    image.onload = function() {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      
      function render() {
        const now = performance.now();
        const deltaTime = (now - lastFrameTime) / 1000; // В секундах
        lastFrameTime = now;
        
        currentTime = (new Date().getTime() - startTime) / 1000;

        // Увеличиваем массу черной дыры
        if(accelerateRef.current){
          // После нажатия "Войти" - медленный рост до огромной массы
          if(curblackholeMass < blackholeMass - 50){
            curblackholeMass += (blackholeMass-curblackholeMass) * 0.006;
          }
        } else {
          // До нажатия - легкое приближение
          if(curblackholeMass < beforeClickMaxMass){
            curblackholeMass += (beforeClickMaxMass-curblackholeMass) * 0.000375;
          }
        }

        // Вращение: при нажатии - 2x скорость, при отпускании - продолжаем с той же позиции
        const currentRotationSpeed = isPressed ? baseRotationSpeed * 2 : baseRotationSpeed;
        rotationAngle += currentRotationSpeed * deltaTime;

        gl.uniform1f(locationOfMass, curblackholeMass*0.00001);
        gl.uniform2f(locationOfMouse, mouse.x, mouse.y);
        gl.uniform1f(locationOfRotation, rotationAngle);

        animationIdRef.current = window.requestAnimationFrame(render);

        const positionLocation = gl.getAttribLocation(program, "a_position");
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }

      render();
    };

    // Cleanup
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        margin: 'auto'
      }}
    />
  );
};

export default BlackHoleWebGL;
